// 近期「远端 tombstone」登记（跨窗口合并的证据通道之一）。
//
// why：桌面壳 main + quickbar 两个 webview 共享 localStorage 但各持内存 store 单例。
// 跨窗口快照合并（lib/cross-window-merge.ts）对「单侧缺行」需要证据裁决：
//   - 本地删除 → outbox 里有待推的 delete op（现成证据）；
//   - **服务器删除**（pull 应用 tombstone）→ 没有任何 outbox 痕迹，而共享 pull cursor
//     已越过该 tombstone 的 row_version，另一窗口**永远不会**再收到它——若合并把
//     「对方快照里还有的行」无证据地并回来，就是永久僵尸行（GC 不回收、无自愈路径）。
// 本模块在 _applyRemote 应用 tombstone 时把实体 id 记进共享 localStorage（RMW），
// 合并时据此把「单侧缺行 + 登记在案」判为删除而非新增。
//
// how：{id → 登记时刻} 映射，TTL 10 分钟 + 上限 500 条（时间近的优先保留）。
// 窗口尺度：跨窗口合并发生在 storage 事件 / 尾随 flush（毫秒~秒级），10 分钟余量
// 极充裕；条目只是 uuid（无 PII）。存储失败静默降级——缺证据的后果是「保守地保行」，
// 与现状一致，不会更糟。单窗口 web/PWA 下写了也没人读，开销可忽略。
const KEY = 'doneline-remote-tombstones-v1';
const TTL_MS = 10 * 60_000;
const CAP = 500;

type Entries = Record<string, number>; // id → epoch ms

function load(): Entries {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Entries = {};
    for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof at === 'number' && Number.isFinite(at)) out[id] = at;
    }
    return out;
  } catch {
    return {};
  }
}

function prune(entries: Entries, now: number): Entries {
  const alive = Object.entries(entries).filter(([, at]) => now - at <= TTL_MS);
  // 超上限时保留时间最近的 CAP 条（旧的先出局）。
  alive.sort((a, b) => b[1] - a[1]);
  return Object.fromEntries(alive.slice(0, CAP));
}

/** _applyRemote 应用了远端 tombstone → 登记实体 id（RMW：先读共享存储再合并写回）。 */
export function recordRemoteTombstones(ids: readonly string[], now = Date.now()): void {
  if (ids.length === 0) return;
  try {
    const entries = load();
    for (const id of ids) entries[id] = now;
    localStorage.setItem(KEY, JSON.stringify(prune(entries, now)));
  } catch {
    // 证据尽力而为：写失败 = 合并时缺证据 → 保守保行（与无本模块时的行为一致）。
  }
}

/** 未过期的远端 tombstone id 集（合并时的删除证据）。 */
export function recentRemoteTombstoneIds(now = Date.now()): Set<string> {
  const entries = load();
  return new Set(
    Object.entries(entries)
      .filter(([, at]) => now - at <= TTL_MS)
      .map(([id]) => id),
  );
}

/** 账号切换 / 清库时的卫生清理（旧账号的 uuid 不可能撞新账号的行，纯卫生）。 */
export function clearRemoteTombstones(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
